import {FC, useEffect, useRef, useState, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {useAppSelector} from '../../../../setup/redux/hooks'
import moment from 'moment'
import Paginator from '../../../components/Paginator'
import { BillingModel } from '../models/BillingModel'
import { BillingFilterModel, createDefaultBillingFilter, validateBillingFilter } from '../models/BillingFilterModel'
import { filterDataChange, getAll, selectBillingFilter, selectBillingList } from '../redux/BillingRedux'
import { toast } from 'react-toastify'
import { UserTransactionAction, UserTransactionDataType, UserTransactionType } from '../constants'

import {KTSVG} from '../../../../_metronic/helpers'
import Tooltip from 'react-tooltip-lite'
import {downloadDocument} from '../redux/BillingApi'
import { DATE_FORMAT } from '../../../shared/constants'

type MonthOption = {
  value: string
  label: string
  startDate: string
  endDate: string
}

const BillingCreditUsageTab: FC = () => { 
  const intl = useIntl()
  const dispatch = useDispatch()
  const didRequest = useRef(false)
  const [localFilter, setLocalFilter] = useState<BillingFilterModel>(createDefaultBillingFilter())
  const [selectedMonth, setSelectedMonth] = useState<string>('thisMonth')
  const [useCustomDates, setUseCustomDates] = useState<boolean>(false)
  const data: BillingModel[] = useAppSelector(selectBillingList)
  const filter: BillingFilterModel = useAppSelector(selectBillingFilter)

  // Generate month options
  const monthOptions = useMemo((): MonthOption[] => {
    const currentMonth = moment()
    const lastMonth = moment().subtract(1, 'month')
    const last3Months = moment().subtract(3, 'months')

    return [
      {
        value: 'thisMonth',
        label: intl.formatMessage({id: 'BILLING.FILTER.THIS_MONTH'}),
        startDate: currentMonth.clone().startOf('month').format(DATE_FORMAT),
        endDate: currentMonth.clone().endOf('month').format(DATE_FORMAT),
      },
      {
        value: 'lastMonth',
        label: intl.formatMessage({id: 'BILLING.FILTER.LAST_MONTH'}),
        startDate: lastMonth.clone().startOf('month').format(DATE_FORMAT),
        endDate: lastMonth.clone().endOf('month').format(DATE_FORMAT),
      },
      {
        value: 'last3Months',
        label: intl.formatMessage({id: 'BILLING.FILTER.LAST_3_MONTHS'}),
        startDate: last3Months.clone().startOf('month').format(DATE_FORMAT),
        endDate: currentMonth.clone().endOf('month').format(DATE_FORMAT),
      },
      {
        value: 'custom',
        label: intl.formatMessage({id: 'BILLING.STATISTICS.CUSTOM'}),
        startDate: '',
        endDate: '',
      },
    ]
  }, [intl])

  useEffect(() => {
    if (!didRequest.current) {
      // Initialize with current month filter
      const currentMonthOption = monthOptions.find(option => option.value === 'thisMonth')
      if (currentMonthOption) {
        const initialFilter = {
          ...createDefaultBillingFilter(),
          startDate: currentMonthOption.startDate,
          endDate: currentMonthOption.endDate,
        }
        setLocalFilter(initialFilter)
        dispatch(filterDataChange(initialFilter))
      }
      dispatch(getAll(undefined))
      didRequest.current = true
    }
  }, [dispatch, monthOptions])

  const handleMonthChange = (monthValue: string) => {
    setSelectedMonth(monthValue)
    const selectedOption = monthOptions.find(option => option.value === monthValue)
    
    if (monthValue === 'custom') {
      setUseCustomDates(true)
      // Don't apply filter immediately for custom dates
      return
    }
    
    setUseCustomDates(false)
    if (selectedOption) {
      const newFilter = {
        ...localFilter,
        startDate: selectedOption.startDate,
        endDate: selectedOption.endDate,
        page: 1,
      }
      setLocalFilter(newFilter)
      dispatch(filterDataChange(newFilter))
    }
  }

  const handleDateRangeChange = (startDate?: string, endDate?: string) => {
    const newFilter = { ...localFilter, startDate, endDate, page: 1 }
    const validation = validateBillingFilter(newFilter)
    
    if (!validation.isValid) {
      validation.errors.forEach(error => {
        toast.error(intl.formatMessage({id: `BILLING.FILTER.ERROR.${error.toUpperCase().replace(' ', '_')}`}, {defaultMessage: error}))
      })
      return
    }
    
    setLocalFilter(newFilter)
    dispatch(filterDataChange(newFilter))
  }

  const handleActionChange = (action: UserTransactionAction) => {
    const newFilter = { ...localFilter, action, page: 1 }
    setLocalFilter(newFilter)
    dispatch(filterDataChange(newFilter))
  }

  const handleClearFilters = () => {
    const defaultFilter = createDefaultBillingFilter()
    setLocalFilter(defaultFilter)
    setSelectedMonth('thisMonth')
    setUseCustomDates(false)
    dispatch(filterDataChange(defaultFilter))
  }

  const handleExportData = () => {
    toast.info(intl.formatMessage({id: 'BILLING.CREDIT_USAGE.EXPORT_INFO'}))
  }
  const handleActionRedirect = (target: any, newTab: boolean = false) => {
    if (newTab) {
      // window.open(target, '_blank')

      downloadDocument(target)
        .then((res) => {
          const url = res.data
          if (url) {
            window.open(url, '_blank')
          } else {
            toast.error(intl.formatMessage({id: 'SERVER.TRANSACTION_NOT_FOUND'}))
          }
        })
        .catch((e) => {
          const message = e.response?.data?.messages[0] ?? e.message
          toast.error(intl.formatMessage({id: 'SERVER.' + message}))
        })
    } else {
      window.location.href = target
    }
  }
  const handlePageChanged = (targetPage: number) => {
    dispatch(filterDataChange({...filter, page: targetPage}))
  }
  const renderItemActions = (item: BillingModel) => {
    let actions: any = []
    let data: any = item.data
    //check item.data is an object or not
    if (item.data && typeof item.data === 'string') {
      data = JSON.parse(item.data)
    }
    if (data && data.type) {
      switch (data.type) {
        case UserTransactionDataType.invoice:
          actions = [
            <Tooltip
              content={intl.formatMessage({id: 'SETTING_REFERENCE.ACTION.invoice'})}
              className='tooltips'
              key={'billing_action'}
            >
              <button
                onClick={() => handleActionRedirect(item.id, true)}
                className='btn btn-icon btn-light-info  btn-active-color-primary btn-sm me-1'
              >
                <KTSVG path='/media/icons/duotune/files/fil026.svg' className='svg-icon-3' />
              </button>
            </Tooltip>,
          ]
          break
        case UserTransactionDataType.orderDetail:
          actions = [
            <Tooltip
              content={intl.formatMessage({id: 'SETTING_REFERENCE.ACTION.Detail'})}
              className='tooltips'
              key={'billing_action'}
            >
              <button
                onClick={() => handleActionRedirect(data.link)}
                className='btn btn-icon btn-light-info  btn-active-color-primary btn-sm me-1'
              >
                <KTSVG path='/media/icons/duotune/files/fil024.svg' className='svg-icon-3' />
              </button>
            </Tooltip>,
          ]
          break
        case UserTransactionDataType.recipe:
          actions = [
            <Tooltip
              content={intl.formatMessage({id: 'SETTING_REFERENCE.ACTION.recipe'})}
              className='tooltips'
              key={'billing_action'}
            >
              <button
                onClick={() => handleActionRedirect(item.id, true)}
                className='btn btn-icon btn-light-info  btn-active-color-primary btn-sm me-1'
              >
                <KTSVG path='/media/icons/duotune/files/fil027.svg' className='svg-icon-3' />
              </button>
            </Tooltip>,
          ]
          break
        default:
          break
      }
    }
    return (
      <div className='d-flex justify-content-end flex-shrink-0'>
        {actions.map((action: any) => action)}
      </div>
    )
  }
  const generateDescriptionWithTypeAndData = (item: BillingModel) => {
    let data: any = item.data
    if (typeof data === 'string') {
      data = JSON.parse(data)
    }
    switch (item.type) {
      case UserTransactionType.CREDIT:
        return intl.formatMessage(
          {id: 'BILLING.MESSAGE.CREDIT'},
          {
            name: intl.formatMessage({id: data?.target ?? ''}),
          }
        )
      case UserTransactionType.REFERRAL:
        return intl.formatMessage({id: 'BILLING.MESSAGE.REFERRAL'})
      case UserTransactionType.EARN:
        return intl.formatMessage(
          {id: 'BILLING.MESSAGE.EARN'},
          {
            reason: intl.formatMessage({id: data?.target}),
          }
        )
      case UserTransactionType.REFUND:
        return intl.formatMessage(
          {id: 'BILLING.MESSAGE.REFUND'},
          {
            order: data?.target ?? '',
          }
        )
      case UserTransactionType.ORDER:
        return intl.formatMessage(
          {id: 'BILLING.MESSAGE.ORDER'},
          {
            order: data?.target ?? '',
          }
        )
      case UserTransactionType.SYNC_ORDER:
        return intl.formatMessage(
          {id: 'BILLING.MESSAGE.SYNC_ORDER'},
          {
            order: data?.target ?? '',
          }
        )
      case UserTransactionType.PAID:
        return intl.formatMessage(
          {id: 'BILLING.MESSAGE.PAID'},
          {
            name: intl.formatMessage({id: data?.target ?? ''}),
          }
        )
      case UserTransactionType.EXPIRED:
        return intl.formatMessage({id: 'BILLING.MESSAGE.EXPIRED'})
    }
  }
  const renderBillingItem = (item: BillingModel) => {
    let data: any = item.data
    if (typeof data === 'string') {
      data = JSON.parse(data)
    }

    return (
      <tr key={item.id} className={item.type === UserTransactionType.CREDIT ? 'table-warning' : ''}>
        <td> {item.id.toString().padStart(10, '0')}</td>
        <td> {generateDescriptionWithTypeAndData(item)} </td>
        <td>{intl.formatMessage({id: 'BILLING.TYPE.' + item.type})}</td>
        <td>
          <span
            className={
              'badge py-3 px-4 fs-7 badge-light-' +
              (item.action === UserTransactionAction.IN ? 'primary' : 'warning')
            }
          >
            {intl.formatMessage({id: 'BILLING.ACTION.' + item.action})}
          </span>
        </td>
        <td className='fw-bolder'>
          <span
            className={
              'badge fs-base badge-light-' +
              (item.action === UserTransactionAction.IN ? 'success' : 'danger')
            }
          >
            {item.action === UserTransactionAction.IN ? '+' : '-'}
            {new Intl.NumberFormat(intl.locale, {style: 'decimal'}).format(item.creditAmount)}
          </span>
        </td>
        <td>
          <span className='text-gray-600 fw-bold fs-6'>
            {item.fiatAmount
              ? new Intl.NumberFormat(intl.locale, {style: 'decimal'}).format(item.fiatAmount) +
                intl.formatMessage({id: data.currency ?? ' '})
              : '-'}
          </span>
        </td>

        <td> {moment(item.createdAt).format(process.env.REACT_APP_DATE_FORMAT)} </td>
        {/* <td>{renderItemActions(item)}</td> */}
      </tr>
    )
  }
  return (
    <>
      {/* Filter Section */}
      <div className='card-header border-0 pt-5 pb-3'>
        <div className='d-flex flex-wrap align-items-end gap-3 w-100'>
          {/* Month Selection */}
          <div className='d-flex flex-column'>
            <label className='form-label fs-6 fw-bolder mb-1'>
              {intl.formatMessage({id: 'BILLING.FILTER.MONTH'})}
            </label>
            <div className='d-flex gap-2'>
              {monthOptions.map((option) => (
                <button
                  key={option.value}
                  type='button'
                  className={`btn btn-sm ${
                    selectedMonth === option.value ? 'btn-primary' : 'btn-light'
                  }`}
                  onClick={() => handleMonthChange(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date Range (shown only when custom is selected) */}
          {useCustomDates && (
            <>
              <div className='d-flex flex-column'>
                <label className='form-label fs-6 fw-bolder mb-1'>
                  {intl.formatMessage({id: 'BILLING.FILTER.START_DATE'})}
                </label>
                <input
                  type='date'
                  className='form-control form-control-sm'
                  style={{ width: '150px' }}
                  value={localFilter.startDate || ''}
                  onChange={(e) => handleDateRangeChange(e.target.value, localFilter.endDate)}
                />
              </div>
              <div className='d-flex flex-column'>
                <label className='form-label fs-6 fw-bolder mb-1'>
                  {intl.formatMessage({id: 'BILLING.FILTER.END_DATE'})}
                </label>
                <input
                  type='date'
                  className='form-control form-control-sm'
                  style={{ width: '150px' }}
                  value={localFilter.endDate || ''}
                  onChange={(e) => handleDateRangeChange(localFilter.startDate, e.target.value)}
                />
              </div>
            </>
          )}

          {/* Transaction Type */}
          <div className='d-flex flex-column'>
            <label className='form-label fs-6 fw-bolder mb-1'>
              {intl.formatMessage({id: 'BILLING.FILTER.TYPE'})}
            </label>
            <select
              className='form-select form-select-sm'
              style={{ width: '120px' }}
              value={localFilter.action || UserTransactionAction.OUT}
              onChange={(e) => handleActionChange(e.target.value as UserTransactionAction)}
            >
              <option value={UserTransactionAction.IN}>
                {intl.formatMessage({id: 'BILLING.ACTION.IN'})}
              </option>
              <option value={UserTransactionAction.OUT}>
                {intl.formatMessage({id: 'BILLING.ACTION.OUT'})}
              </option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className='d-flex gap-2 ms-auto'>
            <button
              className='btn btn-light btn-sm'
              onClick={handleClearFilters}
            >
              {intl.formatMessage({id: 'BILLING.FILTER.CLEAR'})}
            </button>
            {/* <button
              className='btn btn-success btn-sm'
              onClick={handleExportData}
            >
              <i className='bi bi-download me-1'></i>
              {intl.formatMessage({id: 'BILLING.CREDIT_USAGE.EXPORT'})}
            </button> */}
          </div>
        </div>
      </div>

      <div className='table-responsive'>
        {/* begin::Table */}
        <table className='table table-hover table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
          {/* begin::Table head */}
          <thead>
            <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
              <th>{intl.formatMessage({id: 'BILLING.TABLE.ID'})}</th>
              <th className='min-w-300px'>
                {intl.formatMessage({id: 'BILLING.TABLE.DESCRIPTION'})}
              </th>
              <th className='min-w-120px'>{intl.formatMessage({id: 'BILLING.TABLE.CATEGORY'})}</th>
              <th className='min-w-140px'>{intl.formatMessage({id: 'BILLING.TABLE.TYPE'})}</th>
              <th className='min-w-120px'>{intl.formatMessage({id: 'BILLING.TABLE.CREDIT'})}</th>
              <th className='min-w-120px'>{intl.formatMessage({id: 'BILLING.TABLE.FIAT'})}</th>
              <th className='min-w-120px'>{intl.formatMessage({id: 'BILLING.TABLE.TIME'})}</th>
              {/* <th className='min-w-100px text-end'>
                {intl.formatMessage({id: 'BILLING.TABLE.ACTION'})}
              </th> */}
            </tr>
          </thead>
          {/* end::Table head */}
          {/* begin::Table body */}
          <tbody>{data?.map((item: any) => renderBillingItem(item))}</tbody>
          {/* end::Table body */}
        </table>
        {/* end::Table */}
      </div>
      <div className='d-flex justify-content-end pt-10'>
        <Paginator
          currentPage={filter.page}
          totalPages={filter.pageCount || 1}
          onPageChange={handlePageChanged}
        />
      </div>
    </>
  )
}

export default BillingCreditUsageTab